<template>
  <div class="border-t">
    <div class="font-semibold text-black text-[12px] p-[12px] flex items-center justify-between flex-wrap gap-[10px]">
      <span>Data Produk</span>
      <b-button
        variant="ghost"
        size="sm"
        class="!rounded-full hover:bg-[#f4f4f4] !p-0 !size-8 !flex items-center justify-center"
        @click="toggleDetail"
      >
        <span
          class="k-arrow-down-1"
          :class="{ 'rotate-180': showDetail }"
        />
      </b-button>
    </div>
    <b-table
      v-show="showDetail"
      :fields="fields"
      :items="data"
      sticky-header="230px"
      class="!mb-0"
      show-empty
    >
      <template #cell(product)="product">
        <div
          class="flex items-start gap-[10px]"
          style="width: 200px !important;"
        >
          <img
            :src="productImageUrl(product)"
            alt="product"
            width="40"
            height="40"
            class="rounded-lg h-[40px] w-[40px]"
          >
          <div>
            <div class="text-black font-medium text-[12px]">
              {{ product.item.product_name }}
            </div>
            <div class="text-[10px] text-[#F95031]">
              SKU : {{ product.item.product_sku }}
            </div>
          </div>
        </div>
      </template>
      <template #cell(variant)="product">
        <div style="width: 100px !important;">
          <div
            v-for="(variant, index) in (showMore ? product.item.variants : [product.item.variants[0]])"
            :key="index"
            class="flex items-center gap-[10px] mb-[4px]"
          >
            <div>
              <div class="text-black text-[12px]">
                {{ variant.variant }}
              </div>
            </div>
          </div>
          <div v-if="product.item.variants.length > 1">
            <div
              class="cursor-pointer text-[10px] text-[#333333] h-[40px] flex items-center justify-center font-semibold"
              @click="showMore = !showMore"
            >
              <div v-if="showMore">
                Sembunyikan <img
                  src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/arrow-up-2.svg"
                  class="inline w-[15px]"
                  alt="up"
                >
              </div>
              <div v-else>
                Tampilkan <img
                  src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/arrow-down-1.svg"
                  class="inline w-[15px]"
                  alt="down"
                >
              </div>
            </div>
          </div>
        </div>
      </template>
      <template
        #cell(stock)="product"
      >
        <div class="absolute top-0 left-0 bottom-0 pt-[10px] pb-[50px] w-full h-full grid grid-cols-1 gap-[4px]">
          <div
            v-for="(variant, index) in productVariants(product)"
            :key="index"
            class="text-[12px] flex items-center justify-center"
            :class="variant.product_stock <= 10 ? 'text-[#FBA63C]' : 'text-black'"
          >
            <div class="w-[24px]">
              {{ variant.product_stock }}
            </div>
            <img
              v-if="variant.product_stock <= 10"
              v-b-tooltip.hover.top="'Stok produk kamu hampir habis'"
              src="https://storage.googleapis.com/komerce/assets/svg/warning-Info-circle.svg"
              class="mt-[1px]"
              width="14"
              alt="warning"
            >
          </div>
        </div>
      </template>
      <template #empty>
        <div
          v-if="isLoading"
          class="flex items-center justify-center h-[100px]"
        >
          <b-spinner variant="primary" />
        </div>
        <div
          v-else-if="isError"
          class="flex flex-col gap-[8px] justify-center items-center py-[12px]"
        >
          <div class="flex items-center gap-[8px]">
            <span class="k-info-circle text-[20px] text-[#828282]" />
            Terjadi kesalahan saat mengambil data produk
          </div>
          <b-button
            variant="primary"
            size="sm"
            class="!flex items-center gap-[8px]"
            :disabled="isRetry"
            @click="retryFetchProducts"
          >
            Coba Lagi
          </b-button>
        </div>
        <div
          v-else
          class="flex items-center justify-center flex-col gap-[16px] py-[12px]"
        >
          <img
            src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/maximize-circle.svg"
            alt="maximize-circle"
            width="50"
            class="opacity-20"
          >
          <div class="text-[12px] text-[#c2c2c2] text-center">
            <div v-if="item.status_inbound === 'proceed'">
              Pengajuan Inbound kamu sedang dalam proses
            </div>
            <div v-else>
              Kamu belum menyimpan produk di gudang ini. Segera lakukan pengajuan Inbound
            </div>
          </div>
          <b-button
            size="sm"
            :variant="item.status_inbound === 'proceed' ? 'secondary' : 'primary'"
            :disabled="item.status_inbound === 'proceed'"
            @click="goToInbound"
          >
            Ajukan Inbound
          </b-button>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { tableProductFields } from '../config'

export default {
  name: 'WarehouseCardProduct',
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showDetail: false,
      showMore: false,
      fields: tableProductFields,
      data: undefined,
      isLoading: false,
      isError: false,
      isRetry: false,
    }
  },
  methods: {
    /**
     * Get the product image url.
     */
    productImageUrl(product) {
      return product.item.product_image_url || 'https://storage.googleapis.com/komerce/assets/icons/product-placehold.svg'
    },
    /**
     * Get the product variants.
     */
    productVariants(product) {
      return this.showMore ? product.item.variants : [product.item.variants[0]]
    },
    /**
     * Toggle the detail of the warehouse card product with lazily loading the data.
     */
    toggleDetail() {
      this.showDetail = !this.showDetail
      if (!this.data) {
        this.fetchProducts()
      }
    },
    /**
     * Fetch the products list of the warehouse from the server.
     */
    async fetchProducts() {
      this.isLoading = true
      try {
        const res = await this.$http_new.get(`komship/api/v1/subscribe-warehouses/${this.item.warehouse_id}/products`)
        const { data } = res.data
        if (!data || this.item.status_inbound !== 'done') {
          this.data = []
          this.isError = false
          return
        }
        const groupedProducts = this.groupProducts(data)
        this.data = groupedProducts
        this.isError = false
      } catch {
        this.isError = true
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Group the products by product id.
     */
    groupProducts(data) {
      return Object.values(
        data.reduce((acc, item) => {
          const {
            product_id, product_stock, variant, ...rest
          } = item

          if (!acc[product_id]) {
            acc[product_id] = {
              product_id,
              ...rest,
              variants: [],
            }
          }

          acc[product_id].variants.push({
            variant: variant ?? '-',
            product_stock,
          })

          return acc
        }, {}),
      )
    },
    /**
     * Retry the fetch of the products list if error occurs.
     */
    async retryFetchProducts() {
      this.isRetry = true
      await this.fetchProducts()
      this.isRetry = false
    },
    /**
     * Go to the inbound page with the warehouse id.
     */
    goToInbound() {
      this.$router.push({ name: 'ajukan-inbound', query: { warehouse: this.item.warehouse_id } })
    },
  },
}
</script>
